import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { supabasePublic } from '@/integrations/supabase/publicClient';
import { Table } from '@/types/table';

const naturalSort = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export function tablesQueryKey(establishmentId: string | null) {
  return ['tables', establishmentId] as const;
}

export function useTablesQuery(
  establishmentId: string | null,
  useAuthenticatedData = false
) {
  return useQuery({
    queryKey: tablesQueryKey(establishmentId),
    queryFn: async (): Promise<Table[]> => {
      const client = useAuthenticatedData ? supabase : supabasePublic;
      let query = client.from('tables').select('*').order('name');
      if (establishmentId) query = query.eq('establishment_id', establishmentId);

      const { data, error } = await query;
      if (error) throw error;

      const sorted = data ? [...data].sort((a, b) => naturalSort(a.name, b.name)) : [];
      return sorted.map((t: any) => ({
        id: t.id,
        name: t.name,
        capacity: t.capacity,
        minCapacity: t.min_capacity ?? 1,
        isActive: t.is_active,
        isBookable: t.is_bookable ?? true,
        allowsChildren: t.allows_children ?? true,
        allowsStroller: t.allows_stroller ?? true,
        isTerrace: t.is_terrace ?? false,
        assignmentPriority: t.assignment_priority ?? 0,
        zoneId: t.zone_id ?? null,
        notes: t.notes || undefined,
        displayOrder: t.display_order ?? null,
        createdAt: new Date(t.created_at),
      }));
    },
    enabled: Boolean(establishmentId),
    staleTime: 1000 * 60 * 2,
  });
}
